import { parseISO, format, isValid, startOfDay, endOfDay } from 'date-fns'
import { enUS, es } from 'date-fns/locale'
import { capitalize } from 'lodash'
import merge from 'lodash/merge'
import { i18n } from '~/i18n'

const locales = {
  en: enUS,
  es,
}

export const formatISOdate = (date, formatting = 'yyyy-MM-dd HH:mm') => {
  const parsed = parseISO(date)

  if (!isValid(parsed)) {
    return date
  }

  const parsedDate = formatDate(parsed, formatting)

  if (formatting === 'hh:mm a, MMM d, yyyy') {
    return capitalizeFormattedDate(parsedDate)
  }

  return parsedDate
}

export const formatDate = (date, formatting) =>
  capitalize(
    format(date, formatting, {
      locale: locales[i18n.language],
    })
  )

export const formatEmailFilter = (name, value) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return {}
  }

  switch (name) {
    case 'text':
      return {
        fullText: { matches: value },
      }
    case 'meeting_id':
      return {
        emailAgendaItems: {
          some: {
            agendaItem: {
              meetingId: {
                equalTo: Number(value)
              }
            }
          }
        }
      }
    case 'agenda_item_id':
      return {
        emailAgendaItems: {
          some: {
            agendaItemId: {
              equalTo: Number(value)
            }
          }
        }
      }
    case 'users': {
      return {
        fromRedacted: {
          includesInsensitive: value,
        },
      }
    }
    case 'topics':
      return value.length ? {
        emailsTopics: {
          some: {
            topicId: {
              in: value
                .filter((topic) => topic.id)
                .map((topic) => Number(topic.id)),
            },
          },
        },
        emailsTopicsExist: true,
      } : {}
    case 'date_from':
      return {
        date: {
          greaterThanOrEqualTo: startOfDay(value),
        },
      }
    case 'date_to':
      return {
        date: {
          lessThanOrEqualTo: endOfDay(value),
        },
      }
    case 'flagged':
      return value ? {
        flaggedAt: {
          isNull: !value,
        },
      } : {}
    case 'unprocessed':
      return value ? {
        processedAt: {
          isNull: value,
        },
      } : {}
    default:
      return {}
  }
}

export const formatMeetingFilter = (name, value) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return {}
  }

  switch (name) {
    case 'text':
      return {
        fullText: { matches: value },
      }
    case 'date_from':
      return {
        date: {
          greaterThanOrEqualTo: typeof value === 'string' ? parseISO(value) : value,
        },
      }
    case 'date_to':
      return {
        date: {
          lessThanOrEqualTo: typeof value === 'string' ? parseISO(value) : value,
        },
      }
    default:
      return {}
  }
}

export const formatUsersFilter = (name, value) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return {}
  }

  switch (name) {
    case 'date_from':
      return {
        createdAt: {
          greaterThanOrEqualTo: startOfDay(value),
        },
      }
    case 'date_to':
      return {
        createdAt: {
          lessThanOrEqualTo: endOfDay(value),
        },
      }
    default:
      return {
        [name]: {
          includesInsensitive: value,
        },
      }
  }
}

export const formatMessageFilter = (name, value) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return {}
  }

  switch (name) {
    case 'author_id': {
      return {
        authorId: {
          equalTo: +value,
        },
      }
    }
    case 'text':
      return {
        fullText: { matches: value },
      }
    case 'users': {
      const queries = value.split(' ').map((val) => ({
        or: [
          {
            firstName: {
              likeInsensitive: val
            },
          },
          {
            lastName: {
              likeInsensitive: val
            }
          },
        ]
      }))

      return {
        author: {
          or: queries
        }
      }
    }
    case 'topics':
      return value.length ? {
        messagesTopics: {
          some: {
            topicId: {
              in: value
                .filter((topic) => topic.id)
                .map((topic) => Number(topic.id)),
            },
          },
        },
        messagesTopicsExist: true,
      } : {}
    case 'date_from':
      return {
        createdAt: {
          greaterThanOrEqualTo: startOfDay(value),
        },
      }
    case 'date_to':
      return {
        createdAt: {
          lessThanOrEqualTo: endOfDay(value),
        },
      }
    default:
      return {}
  }
}

export const generateFilter = (values, formatter = formatEmailFilter) => Object.entries(values)
  .reduce((acc, [key, value]) => merge(acc, formatter(key, value)),
    {})

export const generateFilterObject = (values, formatter = formatEmailFilter) => {
  const filter = generateFilter(values, formatter)
  if (filter && Object.keys(filter).length) {
    return { filter }
  }
  return {}
}

const capitalizeFormattedDate = (formattedDate) => {
  const aux = formattedDate.split(',')
  aux[1] = ` ${capitalize(aux[1].trim())}`

  return aux.join(',')
}
